import {
  MarketingLayout, Eyebrow, Display, SectionTitle, FocalSentence, Body,
  PrimaryCTA, SecondaryLink, Section, Hairline,
} from "./MarketingLayout";

const STEPS = [
  {
    n: "1",
    title: "Walk into your Residence.",
    body: "Sign in and ARA arranges your rooms. There is no dashboard to configure and no widgets to move. Every family arrives at the same calm shape.",
  },
  {
    n: "2",
    title: "Add the first small things.",
    body: "One family member. One recipe. One appointment. One bill. Nothing is required — the Residence grows at whatever pace your household lives.",
  },
  {
    n: "3",
    title: "Ara begins noticing.",
    body: "As you use the rooms, Ara begins to gently surface what deserves attention — tonight's dinner already sorted, a bill coming up on Friday, someone stretched today.",
  },
  {
    n: "4",
    title: "The mental load lifts, quietly.",
    body: "No streaks. No dashboards. No badges. Just a household that feels a little more held together each week.",
  },
];

const ROOMS = [
  { name: "Today",     line: "The morning brief.", detail: "A single Ara sentence about the day plus the two or three things that deserve attention. Never more." },
  { name: "Family",    line: "Who lives here.", detail: "Each resident's name, role, routines and quiet support needs. The heart of the Residence." },
  { name: "Kitchen",   line: "What are we eating?", detail: "Tonight, this week, a Family Recipe Book and Pantry Intelligence that speaks in judgement, not statistics." },
  { name: "Shopping",  line: "What we need.", detail: "The list that fills itself from recipes and pantry — never a scrap of paper on the fridge again." },
  { name: "The Study", line: "What needs paying.", detail: "Bills, savings, goals and Ara's note — held behind a household PIN so children never see the numbers." },
  { name: "Wellness",  line: "Who needs care.", detail: "Appointments, medications, care plans and quiet observations. Never a mental-health app." },
  { name: "Library",   line: "What needs filing.", detail: "The documents you never want to lose — passports, insurance, tenancies, warranties, medical notes." },
  { name: "Home",      line: "What needs fixing.", detail: "The tap that's dripping, the boiler that's due, the fence you keep putting off." },
];

export default function HowItWorksPage() {
  return (
    <MarketingLayout testid="how-it-works-page">
      <Section testid="how-hero" className="pt-24 sm:pt-32 pb-16">
        <div className="max-w-[820px]">
          <Eyebrow>How it works</Eyebrow>
          <Display className="mt-6">
            The Residence follows the rhythm of a home.
          </Display>
          <FocalSentence className="mt-8 max-w-[42ch]">
            Not the rhythm of software.
          </FocalSentence>
        </div>
      </Section>

      <Hairline />

      <Section testid="how-steps">
        <Eyebrow>Four small steps</Eyebrow>
        <div className="mt-16 space-y-16">
          {STEPS.map(step => (
            <div key={step.n} className="grid grid-cols-[80px_1fr] gap-8 lg:gap-16">
              <p
                className="text-[52px] leading-none tabular-nums"
                style={{ fontFamily: "var(--ara-font-display, 'Fraunces'), 'Times New Roman', serif", color: 'var(--ara-ink-soft, #7A7772)', fontWeight: 400 }}
              >
                {step.n}
              </p>
              <div className="max-w-[52ch] pt-1">
                <p className="text-[22px] sm:text-[26px] leading-[1.2] tracking-[-0.01em]" style={{ fontFamily: "var(--ara-font-display, 'Fraunces'), 'Times New Roman', serif", color: 'var(--ara-ink, #1F1F1F)', fontWeight: 400 }}>
                  {step.title}
                </p>
                <Body className="mt-4">{step.body}</Body>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Hairline />

      <Section testid="how-rooms">
        <Eyebrow>The eight rooms</Eyebrow>
        <SectionTitle className="mt-5 max-w-[26ch]">
          Every room answers one question.
        </SectionTitle>
        <div className="mt-16 divide-y" style={{ borderColor: 'var(--ara-line-soft, rgba(31,31,31,0.06))' }}>
          {ROOMS.map((r, i) => (
            <div
              key={r.name}
              className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6 md:gap-16 py-8"
              style={{ borderBottom: i === ROOMS.length - 1 ? 'none' : '1px solid var(--ara-line-soft, rgba(31,31,31,0.06))' }}
            >
              <div>
                <p className="text-[22px] leading-tight" style={{ fontFamily: "var(--ara-font-display, 'Fraunces'), 'Times New Roman', serif", color: 'var(--ara-ink, #1F1F1F)', fontWeight: 400 }}>
                  {r.name}
                </p>
                <p className="mt-1 italic text-[14px]" style={{ fontFamily: "var(--ara-font-display, 'Fraunces'), 'Times New Roman', serif", color: 'var(--ara-ink-soft, #7A7772)' }}>
                  {r.line}
                </p>
              </div>
              <Body className="max-w-[54ch]">{r.detail}</Body>
            </div>
          ))}
        </div>
      </Section>

      <Hairline />

      <Section testid="how-cta" className="pb-32 text-center">
        <SectionTitle className="max-w-[22ch] mx-auto">
          The rest is quieter than you’d think.
        </SectionTitle>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
          <PrimaryCTA to="/join-beta">Join the waitlist →</PrimaryCTA>
          <SecondaryLink to="/pricing">See pricing</SecondaryLink>
        </div>
      </Section>
    </MarketingLayout>
  );
}
